/** Map directions URLs (APP-1) and the legacy maps.html hand-off page.
 *
 *  `webUrl` is the direct, documented link for each app, used by Take Me There in SIFT and (the
 *  same URLs, built in sales_email_service.py) by the plan emails. A phone opens these in the
 *  app when it is installed.
 *
 *  LEGACY: emails sent before 2026-10-22 linked to maps.html, which used `parseRequest` /
 *  `launch` to open the app. Nothing links there any more; the page stays only so those emails
 *  keep working, and can be deleted once they are stale. It receives ADDRESSES only and builds
 *  the map URLs itself, so it can never be used to send someone to an arbitrary site.
 */
import type { NavApp } from './navPreference';

/** `nav` = one stop in the device's chosen app (see navPreference), asked for if unknown. */
export type Provider = NavApp | 'nav';
export type Platform = 'ios' | 'android' | 'mac' | 'other';

export interface MapsRequest {
  provider: Provider;
  /** Addresses in driving order; the last is the destination. */
  stops: string[];
  /** Where the drive starts; absent = wherever the phone is. */
  origin?: string;
}

/** Google directions take 9 waypoints plus a destination (salesPlanFormat.MAPS_STOPS_PER_LINK). */
export const MAX_STOPS = 10;
const MAX_LEN = 200;
const PROVIDERS: Provider[] = ['google', 'waze', 'apple', 'nav'];
/** Waze and Apple links take one destination; so does a per-stop "Take Me There". */
const SINGLE_STOP: Provider[] = ['waze', 'apple', 'nav'];

const clean = (value: string | null): string | null => {
  const text = Array.from(value ?? '', (ch) => (ch.charCodeAt(0) < 32 ? ' ' : ch)).join('').trim();
  return text && text.length <= MAX_LEN ? text : null;
};

export function parseRequest(search: string): MapsRequest | null {
  const params = new URLSearchParams(search);
  const provider = params.get('p') as Provider | null;
  if (!provider || !PROVIDERS.includes(provider)) return null;
  const stops = params.getAll('s').map(clean);
  if (!stops.length || stops.length > MAX_STOPS || stops.some((s) => s === null)) return null;
  const origin = params.has('o') ? clean(params.get('o')) : undefined;
  if (origin === null) return null;
  if (SINGLE_STOP.includes(provider) && stops.length !== 1) return null;
  return { provider, stops: stops as string[], ...(origin ? { origin } : {}) };
}

export function platformOf(nav: Pick<Navigator, 'userAgent' | 'maxTouchPoints'>): Platform {
  const ua = nav.userAgent;
  if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && (nav.maxTouchPoints ?? 0) > 1)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  if (/Macintosh/.test(ua)) return 'mac';
  return 'other';
}

/** Apple Maps only exists on Apple devices, so it is not offered elsewhere. */
export function appsFor(platform: Platform): NavApp[] {
  return platform === 'ios' || platform === 'mac' ? ['google', 'waze', 'apple'] : ['google', 'waze'];
}

const enc = encodeURIComponent;

/** An unresolved `nav` request falls back to Google, the default app. */
const appOf = (req: MapsRequest): NavApp => (req.provider === 'nav' ? 'google' : req.provider);

const googleQuery = (req: MapsRequest): string => {
  const params = new URLSearchParams({
    api: '1', travelmode: 'driving', destination: req.stops[req.stops.length - 1],
  });
  if (req.origin) params.set('origin', req.origin);
  if (req.stops.length > 1) params.set('waypoints', req.stops.slice(0, -1).join('|'));
  return params.toString();
};

export function webUrl(req: MapsRequest): string {
  const app = appOf(req);
  if (app === 'apple') return `https://maps.apple.com/?daddr=${enc(req.stops[0])}&dirflg=d`;
  if (app === 'waze') return `https://waze.com/ul?q=${enc(req.stops[0])}&navigate=yes`;
  return `https://www.google.com/maps/dir/?${googleQuery(req)}`;
}

/** The app's own link for this platform, or null where the web link already opens the app. */
export function appUrl(req: MapsRequest, platform: Platform): string | null {
  const app = appOf(req);
  if (app === 'apple') {
    return platform === 'ios' || platform === 'mac'
      ? `maps://?daddr=${enc(req.stops[0])}&dirflg=d` : null;
  }
  const fallback = `S.browser_fallback_url=${enc(webUrl(req))};end`;
  if (app === 'waze') {
    if (platform === 'ios') return `waze://?q=${enc(req.stops[0])}&navigate=yes`;
    if (platform === 'android') {
      return `intent://waze.com/ul?q=${enc(req.stops[0])}&navigate=yes`
        + `#Intent;scheme=https;package=com.waze;${fallback}`;
    }
    return null;
  }
  // A multi-stop trip goes to Google's web directions page, which shows every stop and has its
  // own "Open in the app". The app formats for several stops (comgooglemaps daddr=a+to:b, the
  // legacy Android daddr=...+to:) are undocumented and can fail INSIDE the app, where there is
  // nothing to fall back to (APP-1). One stop has documented app links, so it opens the app.
  if (req.stops.length > 1) return null;
  if (platform === 'ios') {
    const saddr = req.origin ? `saddr=${enc(req.origin)}&` : '';
    return `comgooglemaps://?${saddr}daddr=${enc(req.stops[0])}&directionsmode=driving`;
  }
  if (platform === 'android') {
    // The same Maps URL the web uses, handed to the app; Chrome opens the fallback if the app is
    // not installed.
    return `intent://www.google.com/maps/dir/?${googleQuery(req)}`
      + `#Intent;scheme=https;package=com.google.android.apps.maps;${fallback}`;
  }
  return null;
}

/** Try the app; if the page is still on screen after a moment, open the web link instead. */
export function launch(req: MapsRequest, platform: Platform, win: Window = window): void {
  const app = appUrl(req, platform);
  const web = webUrl(req);
  if (!app) {
    win.location.replace(web);
    return;
  }
  let left = false;
  win.document.addEventListener('visibilitychange', () => { left = true; }, { once: true });
  win.location.href = app;
  win.setTimeout(() => {
    if (!left && win.document.visibilityState === 'visible') win.location.replace(web);
  }, 1500);
}
